
import { Component, Prop, Emit, Watch, Vue, Mixins } from 'vue-property-decorator';
import DateTimePicker from '@/components/DateTimePicker.vue';
import Util from '@/utils/util';
import ProductPicker from '@/components/ProductPicker.vue';
import api from '@/api';
import itemMixin from '@/minix/itemMixin';

const { list } = api.mission;

@Component({
  components: { DateTimePicker, ProductPicker },
})
export default class MissionForm extends Mixins(Vue.extend(itemMixin)) {
  @Prop({}) private missionId!: string;

  @Prop({}) private missionName!: string;

  @Prop({}) private startOn!: string;

  @Prop({}) private endOn!: string;

  @Prop({ default: true }) private isEnabled!: boolean;

  @Prop({}) private simpleDescription!: string;

  @Prop({}) private description!: string;

  @Prop({}) private isExternalMission!: boolean;

  @Prop({}) private preMissionId?: string;

  @Prop({}) private preMissionName?: string;

  @Prop({}) private missionAction!: number;

  @Prop({}) private merchantId?: number;

  @Prop({}) private productType?: number;

  @Prop({}) private tagId?: number;

  @Prop({}) private otherPointId?: number;

  @Prop({}) private discountProductId?: string;

  @Prop({}) private browseUrl?: string;

  @Prop({}) private condition?: number;

  @Prop({}) private subCondition?: number;

  @Prop({}) private conditionValue?: number;

  @Prop({}) private rewardPoints?: number;

  @Prop({}) private rewardProducts!: Array<RewardProduct>;

  @Prop({ default: false }) loading!: boolean;

  @Prop({ default: false }) isMissionStarted!: boolean;

  valid: boolean = false;

  form: MissionViewModel = {
    missionName: this.missionName,
    startOn: this.startOn,
    endOn: this.endOn,
    isEnabled: this.isEnabled,
    simpleDescription: this.simpleDescription,
    description: this.description,
    isExternalMission: this.isExternalMission,
    preMissionId: this.preMissionId,
    preMissionName: this.preMissionName,
    missionAction: this.missionAction,
    merchantId: this.merchantId,
    productType: this.productType,
    tagId: this.tagId,
    otherPointId: this.otherPointId,
    discountProductId: this.discountProductId,
    browseUrl: this.browseUrl,
    condition: this.condition,
    subCondition: this.subCondition,
    conditionValue: this.conditionValue,
    rewardPoints: this.rewardPoints,
    rewardProducts: this.rewardProducts || [],
  };

  rules = {
    missionName: [this.requiredRule('標題'), this.maxLengthRule('標題', 100)],
    isEnabled: [this.requiredRule('狀態')],
    simpleDescription: [this.maxLengthRule('任務描述', 200)],
    description: [this.maxLengthRule('任務詳細說明', 1000)],
    missionAction: [this.requiredRule('達成任務行為')],
    isExternalMission: [this.requiredRule('任務分類')],
    browseUrl: [this.maxLengthRule('頁面連結', 1000)],
    condition: [this.conditionRule()],
    conditionValue: [this.conditionValueRule()],
    startOn: [
      this.requiredRule('開始時間'),
      this.lessThenRule(
        '開始時間',
        '結束時間',
        (vm: MissionForm) => vm.form.endOn,
      ),
    ],
    endOn: [
      this.requiredRule('結束時間'),
      this.greaterThenRule(
        '結束時間',
        '開始時間',
        (vm: MissionForm) => vm.form.startOn,
      ),
    ],
  };

  productTypes: Array<{ text: string, value: number }> = [
    { text: '商品', value: 1 },
    { text: '點數', value: 2 },
    { text: '折價券', value: 4 },
  ];

  tagList: Tag[] = [];

  private allSubConditions: Array<{ predicate: (action?: number, condition?: number) => boolean, text: string, value: number | null }> = [
    { predicate: (action, condition) => action === 2 && condition === 1, text: '不限結帳金額（含點／金）', value: null },
    { predicate: (action, condition) => action === 2 && condition === 1, text: '指定結帳金額（含點／金）', value: 1 },
    { predicate: (action, condition) => action === 2 && condition === 1, text: '指定商品數量', value: 2 },
    { predicate: (action, condition) => action === 2 && condition === 2, text: '指定結帳金額（含點／金）', value: 1 },
    { predicate: (action, condition) => action === 2 && condition === 2, text: '指定商品數量', value: 2 },
    { predicate: (action, condition) => action === 2 && condition === 2, text: '指定兌換次數', value: 0 },
    { predicate: (action, condition) => action === 1 && condition === 1, text: '不限換入額度', value: null },
    { predicate: (action, _) => action === 1, text: '指定換入額度', value: 1 },
    { predicate: (action, condition) => action === 1 && condition === 2, text: '指定換入次數', value: 0 },
    { predicate: (action, condition) => action === 3 && condition === 1, text: '不限結帳金額（含點／金）', value: null },
    { predicate: (action, _) => action === 3, text: '指定結帳金額（含點／金）', value: 1 },
    { predicate: (action, condition) => action === 3 && condition === 2, text: '指定結帳次數', value: 0 },
  ]

  private allConditionValueTexts: { [text: string]: (action?: number, subCondition?: number) => boolean } =
    {
      '輸入結帳金額（含點／金）': (action, subCondition) => [2, 3].indexOf(action!) > -1 && subCondition === 1,
      輸入換入額度: (action, subCondition) => action === 1 && subCondition === 1,
      輸入商品數量: (_, subCondition) => subCondition === 2,
      輸入次數: (_, subCondition) => subCondition === 0,
    }

  get showCondition() {
    return !!this.form.missionAction && this.form.missionAction !== 4;
  }

  get showMerchant() {
    return this.form.missionAction && this.form.missionAction < 4;
  }

  get showTag() {
    return this.form.missionAction === 2;
  }

  get showOtherPoint() {
    return this.form.missionAction === 2;
  }

  get showBrowseUrl() {
    return this.form.missionAction === 4;
  }

  get tagSelectOptions() {
    return this.tagList.filter((t: any) => (t.productType & (this.form.productType || 0)) > 0);
  }

  get subConditions() {
    return this.allSubConditions
      .filter(({ predicate }) => predicate(this.form.missionAction, this.form.condition))
      .map(({ text, value }) => ({ text, value }));
  }

  get conditionValueText() {
    const key = Object.keys(this.allConditionValueTexts)
      .find((k) => this.allConditionValueTexts[k](this.form.missionAction, this.form.subCondition));

    return key;
  }

  private get inputs() {
    return {
      missionName: this.missionName,
      startOn: this.startOn,
      endOn: this.endOn,
      isEnabled: this.isEnabled,
      simpleDescription: this.simpleDescription,
      description: this.description,
      isExternalMission: this.isExternalMission,
      preMissionId: this.preMissionId,
      preMissionName: this.preMissionName,
      missionAction: this.missionAction,
      merchantId: this.merchantId,
      productType: this.productType,
      tagId: this.tagId,
      otherPointId: this.otherPointId,
      discountProductId: this.discountProductId,
      browseUrl: this.browseUrl,
      condition: this.condition,
      subCondition: this.subCondition,
      conditionValue: this.conditionValue,
      rewardPoints: this.rewardPoints,
      rewardProducts: this.rewardProducts || [],
    };
  }

  private get datePeriod() {
    const { startOn, endOn } = this.form;
    return {
      startOn,
      endOn,
    };
  }

  isExternalMissions: Array<{ text: string; value: boolean }> = [
    { text: '超級點數任務', value: false },
    { text: '合作任務', value: true },
  ];

  missionActions: Array<{ text: string; value: number }> = [
    { text: '點數換入', value: 1 },
    { text: '兌換商品', value: 2 },
    { text: '線下支付', value: 3 },
    { text: '瀏覽網頁', value: 4 },
  ];

  get preMission() {
    const { preMissionId: missionId, preMissionName: missionName } = this.form;
    return missionId ? { missionId, missionName } as any : undefined;
  }

  set preMission(mission: { missionId: string, missionName: string }) {
    this.form.preMissionId = mission?.missionId;
    this.form.preMissionName = mission?.missionName;
  }

  searchMission: string = '';

  searchMissionResults: Array<{}> = [];

  conditions: Array<{ text: string, value: number }> = [
    { text: '單次', value: 1 },
    { text: '累積', value: 2 },
  ];

  @Watch('searchMission')
  private async searchWatcher(val: MissionListViewModel[]) {
    const {
      data: { data },
    } = await list({ pageNo: 1, pageSize: 10, q: val });

    this.searchMissionResults = data.map((i: MissionListViewModel) => ({
      missionName: i.missionName,
      missionId: i.missionId,
    }))
      .filter((m: any) => m.missionId !== this.missionId);
  }

  @Watch('inputs', { immediate: true })
  private inputsWatcher(val: any) {
    this.form = {
      ...val,
    };
    const { preMissionId: missionId, preMissionName: missionName } = this.form;
    if (missionId) {
      this.searchMissionResults = [{ missionId, missionName }];
    }
  }

  @Watch('form.missionAction')
  private missionActionWatcher() {
    if (this.showMerchant === false) {
      this.form.merchantId = undefined;
    }
    if (this.showTag === false) {
      this.form.productType = undefined;
    }
    if (this.showOtherPoint === false) {
      this.form.otherPointId = undefined;
    }
    if (this.showBrowseUrl === false) {
      this.form.browseUrl = undefined;
    }
    this.form.condition = undefined;
  }

  @Watch('form.condition')
  private conditionWatcher(oldVal: number, newVal: number) {
    this.form.subCondition = undefined;
  }

  @Watch('productType')
  private productTypeWatcher() {
    this.form.tagId = undefined;
  }

  @Watch('conditionValueText')
  private conditionValueTextWatcher(val?: string) {
    if (!val) this.form.conditionValue = undefined;
  }

  @Watch('datePeriod')
  private datePeriodWatcher(val: any) {
    const inputs: any[] = (this.$refs.form as any).inputs.filter(
      (i: any) => i.label === '開始時間' || i.label === '結束時間',
    );
    inputs.forEach((input) => {
      input.validate(true);
    });
  }

  private requiredRule(column: string) {
    return (v: any) => !Util.isNullOrEmpty(v) || `${column} 為必填`;
  }

  private maxLengthRule(column: string, length: number) {
    const message = `${column} 最多 ${length} 字`;
    return (v: string) => !v || v.length <= length || message;
  }

  private lessThenRule(
    column: string,
    column2: string,
    func: (vm: MissionForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v <= c || `${column}要小於${column2}`;
    };
  }

  private greaterThenRule(
    column: string,
    column2: string,
    func: (vm: MissionForm) => any,
  ) {
    return (v: any) => {
      const c = func(this);
      return !v || !c || v >= c || `${column}要大於${column2}`;
    };
  }

  private conditionRule() {
    return (val?: number) => !this.showCondition || !!val || '達成條件必填';
  }

  private conditionValueRule() {
    return (val?: number) => !this.conditionValueText || !!val || `請${this.conditionValueText}`;
  }

  async cancel() {
    await this.$router.back();
  }

  @Emit()
  async submit() {
    const form = this.$refs.form as any as { validate: () => boolean };
    if (form.validate()) {
      const rewardProducts = this.form.rewardProducts.map(
        (m: { productId: string; quantity: number }) => m,
      );
      return {
        ...this.form,
        rewardProducts,
      };
    }
    return null;
  }
}
